import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api/api-context';
import { useJob } from '../api/useJob';
import type { GameProvider, Job, ProviderAccount } from '../api/types';

const PROVIDER_LABELS: Record<GameProvider, string> = {
  chesscom: 'Chess.com',
  lichess: 'Lichess',
};

/** Both sites allow letters, digits, `-` and `_`; Lichess is the wider 2–30 range. */
const USERNAME_PATTERN = /^[A-Za-z0-9_-]{2,30}$/;

function formatWhen(value: string | null | undefined): string {
  if (!value) return 'never';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'never';
  return parsed.toLocaleString();
}

/**
 * Linked chess-site accounts and their game sync (§4, v1.3).
 *
 * Linking is username-only: Chess.com publishes game archives without any grant and
 * Lichess treats OAuth as a rate-limit upgrade, so there is deliberately no "connect
 * your account" consent step to walk through here.
 *
 * `POST /provider-accounts/{id}/syncs` returns 202 with a durable job, tracked through
 * `useJob` exactly like a PGN import. Each sync is incremental — the server only asks
 * the provider for games played since the last successful run — so pressing Sync again
 * is cheap and safe.
 */
export function ConnectedAccountsPanel(): JSX.Element {
  const api = useApi();
  const queryClient = useQueryClient();
  const [provider, setProvider] = useState<GameProvider>('chesscom');
  const [username, setUsername] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  // Keep the accepted job across navigation so returning here resumes polling.
  const savedSync = queryClient.getQueryData<{ job: Job; accountId: string }>(['active-provider-sync']);
  const [jobId, setJobId] = useState<string | null>(savedSync?.job.jobId ?? null);
  const [initialJob, setInitialJob] = useState<Job | null>(savedSync?.job ?? null);
  const [syncingId, setSyncingId] = useState<string | null>(savedSync?.accountId ?? null);
  const [maxGames, setMaxGames] = useState(100);
  const [requesting, setRequesting] = useState(false);
  const requestLock = useRef(false);
  const [now, setNow] = useState(Date.now);
  const { job, ui } = useJob(jobId, initialJob);

  const accounts = useQuery({
    queryKey: ['provider-accounts'],
    queryFn: () => api.listProviderAccounts(),
  });

  // A finished sync changes both the account row (watermark, counters) and the games
  // that feed the explorer and the repertoire suggestions, so refresh all of them.
  const settledOnce = useRef<string | null>(null);
  useEffect(() => {
    if (!ui.isTerminal || jobId === null || settledOnce.current === jobId) return;
    settledOnce.current = jobId;
    setSyncingId(null);
    void queryClient.invalidateQueries({ queryKey: ['provider-accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['games'] });
    void queryClient.invalidateQueries({ queryKey: ['explorer'] });
  }, [ui.isTerminal, jobId, queryClient]);

  useEffect(() => {
    if (jobId === null || ui.isTerminal) return;
    const timer = window.setInterval(() => setNow(Date.now()), 5000);
    return () => window.clearInterval(timer);
  }, [jobId, ui.isTerminal]);

  const link = useMutation({
    mutationFn: (body: { provider: GameProvider; username: string }) =>
      api.linkProviderAccount(body),
    onSuccess: (account) => {
      void onSync(account);
      setUsername('');
      setLinkError(null);
      void queryClient.invalidateQueries({ queryKey: ['provider-accounts'] });
    },
    onError: (error: unknown) => {
      setLinkError(error instanceof Error ? error.message : 'Could not link that account.');
    },
  });

  const unlink = useMutation({
    mutationFn: (providerAccountId: string) => api.unlinkProviderAccount(providerAccountId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['provider-accounts'] }),
  });

  function onLink(event: FormEvent): void {
    event.preventDefault();
    const trimmed = username.trim();
    if (!USERNAME_PATTERN.test(trimmed)) {
      setLinkError('Enter a username of 2–30 letters, digits, hyphens or underscores.');
      return;
    }
    setLinkError(null);
    link.mutate({ provider, username: trimmed });
  }

  async function onSync(account: ProviderAccount): Promise<void> {
    if (requestLock.current) return;
    requestLock.current = true;
    setRequesting(true);
    setSyncError(null);
    setSyncingId(account.providerAccountId);
    try {
      const accepted = await api.syncProviderAccount(
        account.providerAccountId,
        { maxGames },
        crypto.randomUUID(),
      );
      queryClient.setQueryData(['active-provider-sync'], { job: accepted, accountId: account.providerAccountId });
      settledOnce.current = null;
      setInitialJob(accepted);
      setJobId(accepted.jobId);
    } catch (error) {
      setSyncingId(null);
      setSyncError(
        error instanceof Error ? error.message : 'Sync could not be started. Please try again.',
      );
    } finally {
      setRequesting(false);
      requestLock.current = false;
    }
  }

  const syncing = requesting || (jobId !== null && !ui.isTerminal);
  const items = accounts.data ?? [];
  const result = (job?.result ?? null) as Record<string, unknown> | null;

  return (
    <section className="panel" aria-labelledby="accounts-title">
      <h2 id="accounts-title">Connected chess accounts</h2>
      <p className="hint">
        Link your Chess.com or Lichess username to pull your played games in for review.
        Linking starts your first sync. Use Sync now to fetch the next batch of games.
        No password is needed. Linked accounts and imported games belong to this workspace.
      </p>

      <div className="field">
        <label htmlFor="sync-limit">Games per sync</label>
        <select id="sync-limit" value={maxGames} disabled={syncing} onChange={(event) => setMaxGames(Number(event.target.value))}>
          <option value={100}>100 games</option>
          <option value={250}>250 games</option>
          <option value={500}>500 games</option>
        </select>
      </div>
      <form onSubmit={onLink} aria-label="Link a chess account">
        <div className="field">
          <label htmlFor="provider-select">Site</label>
          <select
            id="provider-select"
            value={provider}
            onChange={(e) => setProvider(e.target.value as GameProvider)}
          >
            <option value="chesscom">{PROVIDER_LABELS.chesscom}</option>
            <option value="lichess">{PROVIDER_LABELS.lichess}</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="provider-username">Username</label>
          <input
            id="provider-username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="your-username"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <button type="submit" disabled={username.trim() === '' || link.isPending || syncing}>
          {link.isPending ? 'Linking…' : 'Link & sync account'}
        </button>
      </form>

      {linkError !== null && (
        <p role="alert" className="error">
          {linkError}
        </p>
      )}

      {accounts.isError && <p role="alert">Could not load linked accounts. <button onClick={() => void accounts.refetch()}>Try again</button></p>}
      {unlink.isError && <p role="alert">{unlink.error.message || 'Could not unlink this account. Please try again.'}</p>}
      {accounts.isPending ? (
        <p role="status">Loading linked accounts…</p>
      ) : accounts.isError ? null : items.length === 0 ? (
        <p className="hint">No accounts linked yet.</p>
      ) : (
        <ul className="account-list">
          {items.map((account) => (
            <li key={account.providerAccountId} className="account-row">
              <div>
                <strong>{PROVIDER_LABELS[account.provider]}</strong>{' '}
                <span className="account-handle">{account.username}</span>
                <p className="hint">
                  {account.gamesSynced} game{account.gamesSynced === '1' ? '' : 's'} synced · last
                  sync {formatWhen(account.lastSyncAt)}
                  {account.status === 'error' && account.lastErrorCode
                    ? ` · last attempt failed (${account.lastErrorCode})`
                    : ''}
                </p>
              </div>
              <div className="account-actions">
                <button
                  type="button"
                  onClick={() => void onSync(account)}
                  disabled={syncing}
                  aria-describedby={
                    syncingId === account.providerAccountId ? 'sync-status' : undefined
                  }
                >
                  {syncing && syncingId === account.providerAccountId ? 'Syncing…' : 'Sync now'}
                </button>
                <button
                  type="button"
                  onClick={() => unlink.mutate(account.providerAccountId)}
                  disabled={unlink.isPending || syncing}
                >
                  Unlink
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {syncError !== null && (
        <p role="alert" className="error">
          {syncError}
        </p>
      )}

      {jobId !== null && (
        <div className="job-status">
          <p role="status" id="sync-status">
            {ui.label}
            {ui.progressLabel ? ` — ${ui.progressLabel}` : ''}
          </p>
          {ui.failed && <p role="alert" className="error">
            {job?.error?.code === 'PROVIDER_ACCOUNT_NOT_FOUND'
              ? 'The chess site could not find or access this account. Check the username and link it again.'
              : `Sync failed${job?.error?.code ? ` (${job.error.code})` : ''}. Try Sync now again.`}
          </p>}
          {!ui.isTerminal && job?.status === 'queued' && now - Date.parse(job.createdAt) > 30000 && (
            <p className="hint">Your sync is queued and has not started yet. If it stays queued, the game import worker may be offline.</p>
          )}
          {ui.progressPercent !== null && !ui.isTerminal && (
            <progress value={ui.progressPercent} max={100}>
              {ui.progressPercent}%
            </progress>
          )}
          {ui.succeeded && result !== null && (
            <p className="hint">
              Imported {String(result.games_imported ?? 0)} new game
              {String(result.games_imported ?? 0) === '1' ? '' : 's'}
              {Number(result.games_already_present ?? 0) > 0
                ? ` · ${String(result.games_already_present)} already here`
                : ''}
              {Number(result.games_skipped ?? 0) > 0
                ? ` · ${String(result.games_skipped)} skipped (unsupported variant or unreadable PGN)`
                : ''}
              .
            </p>
          )}
        </div>
      )}
    </section>
  );
}
